import {
  Box,
  HStack,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useSlotRecipe,
} from "shared/components/Spor";

import { createContext, ReactElement, ReactNode, useContext } from "react";

import { InformationOutline18Icon } from "@vygruppen/spor-icon-react";
import {
  isSuccessState,
  SectionState,
  SectionStateContent,
} from "shared/components/states/SectionStateContent";
import { moduleRecipe } from "shared/theme/recipes/module";
import {
  LGHeading,
  LGParagraph,
  MDHeading,
  MDParagraph,
} from "shared/components/typography/TitleStyles";
import { useTheme } from "styled-components";

type ModuleSize = "md" | "lg";

export type ModuleProps = {
  title: ReactNode;
  titleIcon?: ReactNode; /** Icon displayed to the left of the title */
  specification?: ReactNode;
  badgeLeft1?: ReactElement; /** Badge component shown after the title */
  badgeLeft2?: ReactElement; /** Second badge component shown after the title */
  action1?: ReactNode; /** First action button on the right side of the header */
  action2?: ReactNode; /** Second action button on the right side of the header */
  badgeRight?: ReactElement; /** Badge component on the right side of the header */
  infoButtonText?: ReactNode; /** Show information popover button with this text on the upper right corner */
  size?: ModuleSize; /** Controls padding, font size and background. Defaults to "md" */
  disableHorizontalPadding?: boolean; /** Removes left and right padding from the body */
  disableBottomPadding?: boolean; /** Removes bottom padding from the body */
  scrollable?: boolean;
  children?: ReactNode;
  gridArea?: string;
  /**
   * Optional state that adds conditional rendering for the module's content based on its status.
   */
  sectionState?: SectionState;
  className?: string;
  containerName?: string;
};

const ModuleDepthContext = createContext(0);

export const Module = ({
  title,
  titleIcon,
  specification,
  badgeLeft1,
  badgeLeft2,
  action1,
  action2,
  badgeRight,
  infoButtonText,
  size = "md",
  disableHorizontalPadding = false,
  disableBottomPadding = false,
  scrollable = false,
  children,
  gridArea,
  sectionState,
  className,
  containerName = "module",
}: ModuleProps) => {
  const depth = useContext(ModuleDepthContext);
  const theme = useTheme();
  const recipe = useSlotRecipe({ recipe: moduleRecipe });
  const styles = recipe({
    size,
    disableHorizontalPadding,
    disableBottomPadding,
  });

  const showInfoButton = Boolean(infoButtonText) && size === "md";
  const lgModuleNoScrollPadding =
    size === "lg" && !scrollable && !disableHorizontalPadding;
  const hasRightContent = action1 || action2 || badgeRight || showInfoButton;

  const isLarge = size === "lg";

  const childDepth = depth + 1;

  const hideRightContent = !isSuccessState(sectionState);

  return (
    <ModuleDepthContext.Provider value={childDepth}>
      <Box
        gridArea={gridArea}
        className={className}
        css={{
          ...styles.root,
          borderColor: theme.colorOutlineSecondary,
          overscrollBehavior: depth === 0 && scrollable ? "none" : undefined,
          containerType: "inline-size",
          containerName,
        }}
        overflow={scrollable ? undefined : "clip"}
        overflowY={scrollable ? "auto" : undefined}
        data-group
        flexShrink={0}
      >
        <Box
          css={{
            ...styles.header,
            zIndex: 100 - depth,
            paddingBottom: "0",
          }}
        >
          <HStack
            gap="1"
            alignItems="center"
            flexWrap="wrap"
            flexGrow="1"
            flexShrink="1"
            flexBasis="min-content"
          >
            <HStack
              gap="1"
              alignItems="center"
              flexWrap="nowrap"
              flexShrink="1"
              minWidth="0"
            >
              {titleIcon}
              <Box
                display="flex"
                flexWrap="wrap"
                alignItems="baseline"
                columnGap="1"
                minWidth="0"
              >
                {isLarge ? (
                  <LGHeading>{title}</LGHeading>
                ) : (
                  <MDHeading>{title}</MDHeading>
                )}
                {specification && (
                  <Box
                    as="span"
                    display="inline-flex"
                    alignItems="baseline"
                    whiteSpace="nowrap"
                  >
                    {isLarge ? (
                      <>
                        <LGParagraph
                          as="span"
                          aria-hidden
                          style={{ marginRight: "0.25rem" }}
                        >
                          |
                        </LGParagraph>
                        <LGParagraph as="span">{specification}</LGParagraph>
                      </>
                    ) : (
                      <>
                        <MDParagraph
                          as="span"
                          aria-hidden
                          style={{ marginRight: "0.25rem" }}
                        >
                          |
                        </MDParagraph>
                        <MDParagraph as="span">{specification}</MDParagraph>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </HStack>
            {badgeLeft1}
            {badgeLeft2}
          </HStack>
          {hasRightContent && !hideRightContent && (
            <HStack
              gap="1"
              alignItems="center"
              alignSelf="flex-end"
              flexShrink="0"
              marginLeft="auto"
            >
              {action1}
              {action2}
              {badgeRight}
              {showInfoButton && (
                <Popover>
                  <PopoverTrigger>
                    <IconButton
                      variant="ghost"
                      size="xs"
                      icon={<InformationOutline18Icon />}
                      aria-label="Informasjon"
                    />
                  </PopoverTrigger>

                  <PopoverContent>{infoButtonText}</PopoverContent>
                </Popover>
              )}
            </HStack>
          )}
        </Box>
        <SectionStateContent state={sectionState} size={size}>
          <Box
            css={{
              ...styles.body,
              ...(lgModuleNoScrollPadding && { paddingBottom: 0 }),
            }}
          >
            {children}
          </Box>
        </SectionStateContent>
      </Box>
    </ModuleDepthContext.Provider>
  );
};
